// apps/vendor/src/features/orders/detail/ProductionHistoryPanel.tsx
//
// R13 (2026-05-17) — renders the append-only printer-interval log
// for an order item. Sits beside the TransitionTimeline on the order
// detail drawer + page so vendors can see "đơn này in trên DTG#2,
// bắt đầu 09:15, hoàn tất 12:27 (3h12m)" at a glance.

import type { JSX } from 'react';

import { useTranslation } from '../../../lib/i18n/index.js';
import { STATUS_PILL_CLASS } from '../../../lib/ui/statusPill.js';
import type { ProductionHistoryRow } from '../api.js';

interface Props {
  rows: ProductionHistoryRow[];
  isLoading: boolean;
}

function fmtTs(ts: number | null): string {
  if (!ts || ts <= 0) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { hour12: false });
}

function fmtDuration(ms: number | null): string {
  if (!ms || ms <= 0) return '—';
  const secs = Math.round(ms / 1000);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h === 0 && m === 0) return `${secs}s`;
  if (h === 0) return `${m}m`;
  return `${h}h${m}m`;
}

const REASON_I18N_KEY: Record<string, string> = {
  completed: 'productionHistory.reason.completed',
  shipped: 'productionHistory.reason.shipped',
  reassigned: 'productionHistory.reason.reassigned',
  rejected: 'productionHistory.reason.rejected',
  cancelled: 'productionHistory.reason.cancelled',
};

const REASON_CLASS: Record<string, string> = {
  completed: STATUS_PILL_CLASS.success,
  shipped: STATUS_PILL_CLASS.ship,
  reassigned: STATUS_PILL_CLASS.progress,
  rejected: STATUS_PILL_CLASS.danger,
  cancelled: STATUS_PILL_CLASS.neutral,
};

export function ProductionHistoryPanel({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('orders');
  return (
    <section
      data-testid="order-production-history"
      className="rounded-xl border border-pw-border bg-pw-bg p-4 shadow-sm"
    >
      <h3 className="mb-3 text-sm font-semibold text-pw-text">
        {t('productionHistory.title')}
      </h3>
      {isLoading ? (
        <p className="text-xs text-pw-text-muted">{t('productionHistory.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-4 text-center text-xs text-pw-text-muted">
          {t('productionHistory.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <li
              key={r.id}
              data-testid={`production-history-row-${r.id}`}
              className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-pw-text">
                  #{i + 1} · {r.printer_name}
                </span>
                {r.ended_reason ? (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      REASON_CLASS[r.ended_reason] ?? 'bg-pw-surface text-pw-text-muted'
                    }`}
                  >
                    {(() => {
                      const key = REASON_I18N_KEY[r.ended_reason];
                      return key ? t(key) : r.ended_reason;
                    })()}
                  </span>
                ) : (
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_PILL_CLASS.info}`}>
                    {t('productionHistory.inProgress')}
                  </span>
                )}
              </div>
              <dl className="mt-1 grid grid-cols-2 gap-y-0.5 text-pw-text-muted">
                <dt>{t('productionHistory.labelPrinterType')}</dt>
                <dd className="text-pw-text">{r.printer_type}</dd>
                <dt>{t('productionHistory.labelStart')}</dt>
                <dd className="text-pw-text">{fmtTs(r.started_at)}</dd>
                <dt>{t('productionHistory.labelEnd')}</dt>
                <dd className="text-pw-text">
                  {r.ended_at ? fmtTs(r.ended_at) : t('productionHistory.endRunning')}
                </dd>
                <dt>{t('productionHistory.labelDuration')}</dt>
                <dd className="text-pw-text">{fmtDuration(r.duration_ms)}</dd>
              </dl>
              {r.notes ? (
                <p className="mt-1 italic text-pw-text-muted">{r.notes}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export default ProductionHistoryPanel;
